/**
 * TagFilter Component for Voice2Doc Tags System
 * 
 * Features:
 * - Multi-select tags in popover
 * - Search tags by name
 * - Badge showing selected count
 * - Mobile-friendly design
 * - Integration with responsive filters
 * - Clear all functionality
 */

import React, { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from "react-i18next";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Filter, X, Search, Hash } from 'lucide-react';
import { Tag } from '@/integrations/supabase/types';
import { TagChip } from '@/components/records/TagChip';

export interface TagFilterProps {
  availableTags: Tag[];
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxDisplayTags?: number;
  variant?: 'default' | 'outline' | 'ghost';
  showCount?: boolean;
}

const TagFilter = React.forwardRef<HTMLButtonElement, TagFilterProps>(
  (
    {
      availableTags,
      selectedTagIds,
      onTagsChange,
      placeholder, // Will handle default in component body
      className,
      disabled = false,
      maxDisplayTags = 3,
      variant = "outline",
      showCount = true,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { t } = useTranslation();

    const actualPlaceholder = placeholder || t('records.tag_filter.placeholder');

    // Filter available tags based on search query
    const filteredTags = useMemo(() => {
      if (!searchQuery.trim()) return availableTags;

      return availableTags.filter(tag =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [availableTags, searchQuery]);

    // Get selected tags objects
    const selectedTags = useMemo(() => {
      return availableTags.filter(tag => selectedTagIds.includes(tag.id));
    }, [availableTags, selectedTagIds]);

    // Handle individual tag selection
    const handleTagToggle = (tagId: string, checked: boolean) => {
      if (checked) {
        onTagsChange([...selectedTagIds, tagId]);
      } else {
        onTagsChange(selectedTagIds.filter(id => id !== tagId));
      }
    };

    // Handle select all
    const handleSelectAll = () => {
      const allVisibleIds = filteredTags.map(tag => tag.id);
      const newSelected = [...new Set([...selectedTagIds, ...allVisibleIds])];
      onTagsChange(newSelected);
    };

    // Handle clear all
    const handleClearAll = () => {
      onTagsChange([]);
      setSearchQuery('');
    };

    // Handle clear visible
    const handleClearVisible = () => {
      const visibleIds = filteredTags.map(tag => tag.id);
      const newSelected = selectedTagIds.filter(id => !visibleIds.includes(id));
      onTagsChange(newSelected);
    };

    // Clear search when popover closes
    useEffect(() => {
      if (!open) {
        setSearchQuery('');
      }
    }, [open]);

    const selectedCount = selectedTagIds.length;
    const hasSelection = selectedCount > 0;

    // Display text
    const getDisplayText = () => {
      if (selectedCount === 0) {
        return actualPlaceholder;
      }

      if (selectedCount === 1) {
        return selectedTags[0]?.name || actualPlaceholder;
      }

      return t('records.tag_filter.selected_count_button', { count: selectedCount });
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={hasSelection ? "default" : variant}
            className={cn(
              "h-9 justify-between font-normal",
              !hasSelection && "text-muted-foreground",
              className
            )}
            disabled={disabled}
            {...props}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Hash className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{getDisplayText()}</span>
            </div>

            <div className="flex items-center gap-1 ml-2">
              {hasSelection && showCount && (
                <Badge
                  variant="secondary"
                  className="h-5 px-1.5 text-xs font-medium"
                >
                  {selectedCount}
                </Badge>
              )}
              <Filter className="h-4 w-4 flex-shrink-0" />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-80 p-0"
          side="bottom"
          align="start"
          sideOffset={4}
        >
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h4 className="font-medium leading-none">{actualPlaceholder}</h4>
              {hasSelection && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={handleClearAll}
                >
                  {t('records.tag_filter.clear_all')}
                </Button>
              )}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('records.tag_filter.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
            </div>

            {/* Selected tags preview */}
            {selectedTags.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('records.tag_filter.selected_title')} ({selectedTags.length})
                </div>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-auto">
                  {selectedTags.slice(0, maxDisplayTags).map((tag) => (
                    <TagChip
                      key={tag.id}
                      tag={tag}
                      size="sm"
                      maxWidth="120px"
                      onDelete={(tagId) => handleTagToggle(tagId, false)}
                    />
                  ))}
                  {selectedTags.length > maxDisplayTags && (
                    <Badge variant="secondary" className="text-xs">
                      +{selectedTags.length - maxDisplayTags}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Bulk actions */}
            {filteredTags.length > 0 && (
              <div className="flex justify-between text-xs">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0"
                  onClick={handleSelectAll}
                >
                  {t('records.tag_filter.select_all_visible')}
                </Button>
                {searchQuery && selectedTagIds.some(id =>
                  filteredTags.some(tag => tag.id === id)
                ) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0"
                      onClick={handleClearVisible}
                    >
                      {t('records.tag_filter.clear_visible')}
                    </Button>
                  )}
              </div>
            )}

            {/* Tags list */}
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {filteredTags.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    {searchQuery ? t('records.tag_filter.no_tags_found') : t('records.tag_filter.no_tags_created')}
                  </div>
                ) : (
                  filteredTags.map((tag) => {
                    const isSelected = selectedTagIds.includes(tag.id);

                    return (
                      <div
                        key={tag.id}
                        className={cn(
                          "flex items-center space-x-2 rounded-md p-2 hover:bg-accent cursor-pointer transition-colors",
                          isSelected && "bg-accent/50"
                        )}
                        onClick={() => handleTagToggle(tag.id, !isSelected)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleTagToggle(tag.id, Boolean(checked))
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <TagChip
                            tag={tag}
                            size="sm"
                            maxWidth="200px"
                            className="pointer-events-none"
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>

            {/* Footer */}
            {selectedCount > 0 && (
              <>
                <Separator />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t('records.tag_filter.selected_count_badge', { count: selectedCount, total: availableTags.length })}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => setOpen(false)}
                  >
                    {t('records.tag_filter.done')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

TagFilter.displayName = "TagFilter";

export { TagFilter };